import React, { useState } from "react";
import {
  IconButton,
  Popover,
  Card,
  CardContent,
  Typography,
  Avatar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  useMediaQuery,
  useTheme,
  Box,
  Button,
} from "@mui/material";
import { Person, ExitToApp, AccountCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { API_BASE } from "../../services";

const UserMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoutSubmitting, setLogoutSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const absolutize = (url) => {
    if (!url || typeof url !== "string") return null;
    if (/^https?:\/\//i.test(url)) return url;
    const base = API_BASE.replace(/\/+$/, "");
    return `${base}/${url.replace(/^\/+/, "")}`;
  };

  const avatar = absolutize(user?.avatar);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogOut = async () => {
    handleClose();
    const result = await logout();
    if (result?.requiresConfirmation) {
      setLogoutConfirmOpen(true);
      return;
    }
    navigate("/login", { replace: true });
  };

  const handleConfirmLogout = async () => {
    setLogoutSubmitting(true);
    try {
      await logout({ confirmedRelease: true });
      setLogoutConfirmOpen(false);
      navigate("/login", { replace: true });
    } finally {
      setLogoutSubmitting(false);
    }
  };

  const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const username = user?.username || "";
  const email = user?.email || "";

  const initials =
    `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase() ||
    "U";

  return (
    <>
      <Tooltip title="Menu de usuario">
        <IconButton
          onClick={handleOpen}
          sx={{
            color: "white",
            "&:hover": { bgcolor: "rgba(117,123,43,0.08)", color: "secondary.main" },
          }}
        >
          <Person />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Card
          sx={{
            width: isMobile ? 240 : 290,
            borderRadius: 2,
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: "0 18px 50px rgba(16,24,40,0.12)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: 4,
              background: (t) => `linear-gradient(90deg, ${t.palette.secondary.main}, ${t.palette.primary.main})`,
            }}
          />
          <CardContent sx={{ textAlign: "left" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Avatar
                src={avatar || undefined}
                sx={{
                  width: isMobile ? 46 : 54,
                  height: isMobile ? 46 : 54,
                  bgcolor: "secondary.main",
                  color: "secondary.contrastText",
                  fontWeight: 900,
                }}
              >
                {!avatar && initials}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900 }} noWrap>
                  {fullName || "Usuario"}
                </Typography>
                {username ? (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", lineHeight: 1.2 }}>
                    @{username}
                  </Typography>
                ) : null}
                <Typography sx={{ color: "text.secondary", fontSize: 12.5 }} noWrap>
                  {email}
                </Typography>
              </Box>
            </Box>

            <Divider />

            <List dense sx={{ pt: 1 }}>
              <ListItemButton
                onClick={() => {
                  handleClose();
                  navigate("/app/profile");
                }}
                sx={{
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "rgba(117,123,43,0.08)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "primary.main" }}>
                  <AccountCircle />
                </ListItemIcon>
                <ListItemText primary="Ver perfil" />
              </ListItemButton>

              <ListItemButton
                onClick={handleLogOut}
                sx={{
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "rgba(243,105,77,0.10)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "secondary.main" }}>
                  <ExitToApp />
                </ListItemIcon>
                <ListItemText primary="Cerrar sesion" />
              </ListItemButton>
            </List>
          </CardContent>
        </Card>
      </Popover>

      <Dialog open={logoutConfirmOpen} onClose={() => !logoutSubmitting && setLogoutConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ventas sin finalizar</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Tienes una o más ventas sin finalizar. Si sales, se liberarán los productos captados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutConfirmOpen(false)} disabled={logoutSubmitting}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmLogout} disabled={logoutSubmitting}>
            {logoutSubmitting ? "Saliendo..." : "Liberar y salir"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserMenu;
